import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import _ from 'lodash';

import { SelectField } from 'src/components/form';
import { MACHINING_OPTIONS } from 'src/constants/quotation/optionSelect';
import { FormValue } from '../useForm';
import { getVisiableMachining } from 'src/utils/quotation';
import { MACHINING_TOLERANCE } from 'src/constants/app';
import { useToleranceGroupFilter } from 'src/hooks/useToleranceGroupFilter';
import { Typography } from 'antd';

type Props = {
  index: number;
  formInfo: UseFormReturn<FormValue, any>;
  dataReEstimate: any;
};

const SizeInput: React.FC<Props> = ({ index, formInfo, dataReEstimate }) => {
  const { control, getValues, setValue, watch } = formInfo;
  const { t } = useTranslation();

  const [showMachiningTolerance, setShowMachiningTolerance] = useState(false);

  // Watch tolerance fields for group locking
  const watchedThickness = watch(`priceQuotes.${index}.machining_thickness`);
  const watchedWidth = watch(`priceQuotes.${index}.machining_width`);
  const watchedLength = watch(`priceQuotes.${index}.machining_length`);
  const watchedOutside = watch(
    `priceQuotes.${index}.machining_outside_diameter`,
  );
  const toleranceOptions = watch(`priceQuotes.${index}.toleranceOptions`);
  const isResetByMachiningMethod = watch(
    `priceQuotes.${index}.isResetByMachiningMethod`,
  );
  const watchedMachiningItem = watch(`priceQuotes.${index}.machiningItem`);
  const numberOfInputDimensions = watch(
    `priceQuotes.${index}.numberOfInputDimensions`,
  );

  const checkVisiableMachiningInitial = (tolerance) =>
    getVisiableMachining(
      dataReEstimate?.isCreate
        ? getValues(`priceQuotes.${index}.shape`)
        : dataReEstimate?.keijoL3Cd,
      watchedMachiningItem?.value as string,
      tolerance,
    );

  // Use custom hook for tolerance group filtering
  const { filteredOptions: machiningOptions } = useToleranceGroupFilter({
    product: dataReEstimate?.product || '',
    toleranceOptions,
    watchedValues: {
      thickness: watchedThickness,
      width: watchedWidth,
      length: watchedLength,
      outside: watchedOutside,
    },
  });

  const machiningThicknessOptions = machiningOptions.thickness;
  const machiningWidthOptions = machiningOptions.width;
  const machiningLengthOptions = machiningOptions.length;
  const machiningOutsideOptions = machiningOptions.outside;

  const isVisiableMachining = (tolerance, machiningOptions) => {
    //before call API tolerance : checkVisiableMachiningInitial
    //after call API tolerance : check !_.isEmpty(machiningOptions)
    return (
      checkVisiableMachiningInitial(tolerance) &&
      (toleranceOptions?.apiCalled ? !_.isEmpty(machiningOptions) : true)
    );
  };

  const isVisiableMachiningThickness = isVisiableMachining(
    MACHINING_TOLERANCE.PLATE_THICKNESS,
    machiningThicknessOptions,
  );
  const isVisiableMachiningWidth = isVisiableMachining(
    MACHINING_TOLERANCE.WIDTH,
    machiningWidthOptions,
  );
  const isVisiableMachiningLength = isVisiableMachining(
    MACHINING_TOLERANCE.LENGTH,
    machiningLengthOptions,
  );
  const isVisiableMachiningOutside = isVisiableMachining(
    MACHINING_TOLERANCE.OUTSIDE_DIAMETER,
    machiningOutsideOptions,
  );
  const isVisiableMachiningInside = checkVisiableMachiningInitial(
    MACHINING_TOLERANCE.INSIDE_DIAMETER,
  );
  useEffect(() => {
    if (isResetByMachiningMethod) return;
    if (!dataReEstimate) {
      isVisiableMachiningThickness &&
        setValue(
          `priceQuotes.${index}.machining_thickness`,
          machiningThicknessOptions?.[0]?.value || '',
        );
      isVisiableMachiningWidth &&
        setValue(
          `priceQuotes.${index}.machining_width`,
          machiningWidthOptions?.[0]?.value || '',
        );
      isVisiableMachiningLength &&
        setValue(
          `priceQuotes.${index}.machining_length`,
          machiningLengthOptions?.[0]?.value || '',
        );
      isVisiableMachiningOutside &&
        setValue(
          `priceQuotes.${index}.machining_outside_diameter`,
          machiningOutsideOptions?.[0]?.value || '',
        );
      isVisiableMachiningInside &&
        setValue(
          `priceQuotes.${index}.machining_inside_diameter`,
          MACHINING_OPTIONS[0].value,
        );
    } else {
      isVisiableMachiningThickness &&
        setValue(
          `priceQuotes.${index}.machining_thickness`,
          dataReEstimate?.optc001 || '',
        );
      isVisiableMachiningWidth &&
        setValue(
          `priceQuotes.${index}.machining_width`,
          dataReEstimate?.optc002 || '',
        );
      isVisiableMachiningLength &&
        setValue(
          `priceQuotes.${index}.machining_length`,
          dataReEstimate?.optc003 || '',
        );
      isVisiableMachiningOutside &&
        setValue(
          `priceQuotes.${index}.machining_outside_diameter`,
          dataReEstimate?.optc004 || '',
        );
    }
  }, [toleranceOptions, isResetByMachiningMethod]);

  const clearMachiningToleranceValue = () => {
    !isVisiableMachiningThickness &&
      setValue(`priceQuotes.${index}.machining_thickness`, '');
    !isVisiableMachiningWidth &&
      setValue(`priceQuotes.${index}.machining_width`, '');
    !isVisiableMachiningLength &&
      setValue(`priceQuotes.${index}.machining_length`, '');
    !isVisiableMachiningOutside &&
      setValue(`priceQuotes.${index}.machining_outside_diameter`, '');
    !isVisiableMachiningInside &&
      setValue(`priceQuotes.${index}.machining_inside_diameter`, '');
  };

  useEffect(() => {
    const isShowMachiningTolerance = _.some([
      isVisiableMachiningThickness,
      isVisiableMachiningWidth,
      isVisiableMachiningLength,
      isVisiableMachiningOutside,
      isVisiableMachiningInside,
    ]);
    setShowMachiningTolerance(isShowMachiningTolerance);
    setValue(
      `priceQuotes.${index}.isShowMachiningTolerance`,
      isShowMachiningTolerance,
    );
  }, [
    isVisiableMachiningThickness,
    isVisiableMachiningWidth,
    isVisiableMachiningLength,
    isVisiableMachiningOutside,
    isVisiableMachiningInside,
  ]);

  useEffect(() => {
    clearMachiningToleranceValue();
  }, [watchedMachiningItem?.value]);

  return (
    <div className="options-size">
      {showMachiningTolerance && (
        <div className="options-size__machining-tolerance">
          {(isVisiableMachiningThickness ||
            isVisiableMachiningWidth ||
            isVisiableMachiningLength ||
            isVisiableMachiningOutside ||
            isVisiableMachiningInside) && (
            <Typography.Text className="options-size__label">
              {t('multiple_quotes_page.processing_tolerance')}
            </Typography.Text>
          )}
          <div className="options-size__select-wrap">
            {(isVisiableMachiningThickness ||
              isVisiableMachiningWidth ||
              isVisiableMachiningLength) && (
              <div className="options-size__select-group">
                <div className="options-size__select-item">
                  {isVisiableMachiningThickness && (
                    <SelectField<FormValue>
                      showErrorTooltip
                      controller={{
                        control,
                        name: `priceQuotes.${index}.machining_thickness`,
                      }}
                      selectProps={{
                        placeholder: t(
                          'pricing_page.machining_tolerance.plate_thickness',
                        ),
                        options: machiningThicknessOptions,
                        allowClear: true,
                      }}
                      className="options-size__select"
                    />
                  )}
                </div>
                {Number(numberOfInputDimensions) !== 2 && (
                  <div className="options-size__select-item">
                    {isVisiableMachiningWidth && (
                      <SelectField<FormValue>
                        showErrorTooltip
                        controller={{
                          control,
                          name: `priceQuotes.${index}.machining_width`,
                        }}
                        selectProps={{
                          placeholder: t(
                            'pricing_page.machining_tolerance.width',
                          ),
                          options: machiningWidthOptions,
                          allowClear: true,
                        }}
                        className="options-size__select"
                      />
                    )}
                  </div>
                )}
                <div className="options-size__select-item">
                  {isVisiableMachiningLength && (
                    <SelectField<FormValue>
                      showErrorTooltip
                      controller={{
                        control,
                        name: `priceQuotes.${index}.machining_length`,
                      }}
                      selectProps={{
                        placeholder: t(
                          'pricing_page.machining_tolerance.length',
                        ),
                        options: machiningLengthOptions,
                        allowClear: true,
                      }}
                      className="options-size__select"
                    />
                  )}
                </div>
              </div>
            )}
            {(isVisiableMachiningOutside || isVisiableMachiningInside) && (
              <div className="options-size__select-group options-size__select-group-bottom">
                <div className="options-size__select-item">
                  {isVisiableMachiningOutside && (
                    <SelectField<FormValue>
                      showErrorTooltip
                      controller={{
                        control,
                        name: `priceQuotes.${index}.machining_outside_diameter`,
                      }}
                      selectProps={{
                        placeholder: t(
                          'pricing_page.machining_tolerance.outside_diameter',
                        ),
                        options: machiningOutsideOptions,
                        allowClear: true,
                      }}
                    />
                  )}
                </div>
                <div className="options-size__select-item">
                  {isVisiableMachiningInside && (
                    <SelectField<FormValue>
                      showErrorTooltip
                      controller={{
                        control,
                        name: `priceQuotes.${index}.machining_inside_diameter`,
                      }}
                      selectProps={{
                        placeholder: t(
                          'pricing_page.machining_tolerance.inside_diameter',
                        ),
                        options: MACHINING_OPTIONS,
                        allowClear: true,
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeInput;
